import React, { useState, useRef, useEffect } from 'react';
import { FiSave, FiCheckCircle, FiXCircle, FiPaperclip, FiDownload, FiRotateCcw, FiMapPin, FiFileText, FiEye, FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup, Circle, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Api from '../../Api';
import Button from '../Button';
import Input from '../Input';
import Modal from '../Modal';
import '../CompanyForm/style.css';
import '../../pages/CompanyManager/style.css';
import './style.css';

// Fix for Leaflet default icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function MapUpdater({ bounds, center }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 1) {
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (center) {
      map.setView(center, 15);
    }
  }, [bounds, center, map]);
  return null;
}

export default function AdjustmentAnalysisForm({ data, companyId, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedTimeTab, setSelectedTimeTab] = useState(0);
  const [error, setError] = useState('');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [analysis, setAnalysis] = useState({
    status: data.status === 'pending' ? 'approved' : data.status,
    rejection_reason: data.rejection_reason || '',
  });

  const rejectionReasonRef = useRef(null);

  const isReadOnly = data.status !== 'pending';

  const parseRequestedTimes = (times) => {
    if (!times) return Array(4).fill('');
    let list = [];
    
    if (typeof times === 'string') {
      if (times.trim().startsWith('[') && times.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(times);
          list = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
             list = times.split(',').map(t => t.trim());
        }
      } else {
         list = times.split(',').map(t => t.trim());
      }
    } else if (Array.isArray(times)) {
      list = times;
    }

    const timeStrings = list.map(item => {
      if (typeof item === 'object' && item !== null) {
        return item.time || '';
      }
      if (item === null || item === undefined) return '';
      return String(item).trim();
    });

    const result = [];
    for (let i = 0; i < 4; i++) {
        result.push(timeStrings[i] || '');
    }
    return result;
  };

  const [localRequestedTimes, setLocalRequestedTimes] = useState(parseRequestedTimes(data.requested_times));
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (analysis.status === 'rejected' && rejectionReasonRef.current) {
      rejectionReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [analysis.status]);

  function handleStatusChange(status) {
    setAnalysis(prev => ({ ...prev, status }));
  }

  function handleReasonChange(e) {
    setAnalysis(prev => ({ ...prev, rejection_reason: e.target.value }));
  }

  function handleEditClick(index) {
    if (isReadOnly) return;
    setEditingIndex(index);
    setEditValue(localRequestedTimes[index] || '');
  }

  function handleEditSave(index) {
    const newTimes = [...localRequestedTimes];
    newTimes[index] = editValue;
    setLocalRequestedTimes(newTimes);
    setEditingIndex(null);
    setEditValue('');
  }

  function handleEditCancel() {
    setEditingIndex(null);
    setEditValue('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (analysis.status === 'rejected' && !analysis.rejection_reason.trim()) {
      setError('Por favor, informe o motivo da reprovação.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await Api.updateAdjustment(companyId, data.id, {
        ...data,
        status: analysis.status,
        rejection_reason: analysis.status === 'rejected' ? analysis.rejection_reason : null,
        requested_times: localRequestedTimes,
      });
      toast.success('Solicitação atualizada com sucesso!');
      onSuccess();
    } catch (err) {
      setError('Erro ao salvar análise.');
      toast.error('Erro ao salvar análise.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRevert() {
    setShowRevertModal(false);
    setLoading(true);
    setError('');

    try {
      await Api.updateAdjustment(companyId, data.id, {
        ...data,
        status: 'pending',
        rejection_reason: null,
      });
      toast.success('Status revertido para Pendente!');
      onSuccess();
    } catch (err) {
      setError('Erro ao reverter status.');
      toast.error('Erro ao reverter status.');
    } finally {
      setLoading(false);
    }
  }

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
  };

  const handleTabChange = (index) => {
    if (index === selectedTimeTab) return;
    setMapLoading(true);
    setTimeout(() => {
      setSelectedTimeTab(index);
      setMapLoading(false);
    }, 300);
  };

  const parseTimeObjects = (times) => {
    if (!times) return [];
    let list = [];
    if (typeof times === 'string') {
      try {
        const parsed = JSON.parse(times);
        if (Array.isArray(parsed)) list = parsed;
      } catch (e) {
        return [];
      }
    } else if (Array.isArray(times)) {
      list = times;
    }
    // Filter to ensure objects with time, even if other fields are null
    return list.filter(item => typeof item === 'object' && item !== null && item.time);
  };

  const parseOriginalTimes = (times) => {
    if (!times) return [];
    let list = [];
    
    // Se for string, tenta fazer parse
    if (typeof times === 'string') {
      // Verifica se parece um JSON array stringificado
      if (times.trim().startsWith('[') && times.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(times);
          list = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          // Se falhar o JSON parse, tenta split por vírgula
          list = times.split(',').map(t => t.trim()).filter(Boolean);
        }
      } else {
        // String simples separada por vírgula
        list = times.split(',').map(t => t.trim()).filter(Boolean);
      }
    } else if (Array.isArray(times)) {
      list = times;
    }

    // Normaliza para extrair apenas o horário se for objeto
    const timeStrings = list.map(item => {
      if (typeof item === 'object' && item !== null) {
        return item.time || '';
      }
      return String(item).trim();
    }).filter(Boolean);
    
    // Remover duplicatas e ordenar e limitar a 4
    return [...new Set(timeStrings)].sort().slice(0, 4);
  };

  const originalTimesList = parseOriginalTimes(data.original_times);

  const timeObjects = parseTimeObjects(data.original_times);
  const selectedTimeData = timeObjects[selectedTimeTab];
  
  let mapDistance = 0;
  let isInsideFence = false;
  let mapCenter = [-15.793889, -47.882778]; // Default Brasilia
  let mapBounds = [];
  let hasLocation = false;
  let hasStation = false;

  if (selectedTimeData) {
    const lat = parseFloat(selectedTimeData.latitude);
    const lon = parseFloat(selectedTimeData.longitude);
    const stationLat = parseFloat(selectedTimeData.station_latitude);
    const stationLon = parseFloat(selectedTimeData.station_longitude);
    
    if (!isNaN(lat) && !isNaN(lon)) {
        hasLocation = true;
        mapCenter = [lat, lon];
        mapBounds.push([lat, lon]);
        
        if (!isNaN(stationLat) && !isNaN(stationLon)) {
            hasStation = true;
            mapDistance = calculateDistance(lat, lon, stationLat, stationLon);
            isInsideFence = mapDistance <= (selectedTimeData.fence_radius_km || 0);
            mapCenter = [(lat + stationLat) / 2, (lon + stationLon) / 2];
            mapBounds.push([stationLat, stationLon]);
        }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <Modal
          isOpen={showRevertModal}
          onClose={() => setShowRevertModal(false)}
          title="Confirmar Reversão"
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja reverter esta análise para o status <strong>Pendente</strong>?
            </p>
            <p>Essa ação não poderá ser desfeita.</p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setShowRevertModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleRevert}
              disabled={loading}
            >
              {loading ? 'Revertendo...' : 'Reverter'}
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title="Visualizar Comprovante"
          size="lg"
        >
          <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
            {data.attachment && (
              <img 
                src={data.attachment} 
                alt="Comprovante" 
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
              />
            )}
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setShowPreviewModal(false)}
            >
              Fechar
            </Button>
          </div>
        </Modal>

        <div className="adjustment-analysis-grid">
            {/* Left Column: Data */}
            <div>
                <h3 className="section-title" style={{ marginTop: '0px' }}>Dados da Solicitação</h3>
                
                <div className="form-grid">
                    <div className="full-width">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Funcionário</label>
                                <div style={{ fontSize: '1rem', fontWeight: 500 }}>{data.employee?.name || '-'}</div>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                                <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Competência</label>
                                <div style={{ fontSize: '1rem', fontWeight: 500 }}>{new Date(data.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</div>
                            </div>
                        </div>
                    </div>

                    <div className="full-width" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Comparativo de Horários</label>
                        <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '120px 1fr 1fr', 
                        gap: '1px',
                        backgroundColor: 'var(--border-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        overflow: 'hidden'
                        }}>
                        {/* Header */}
                        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', fontWeight: 600, textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>Tipo</div>
                        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', fontWeight: 600, textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>Original</div>
                        <div style={{ padding: '10px', backgroundColor: '#e3f2fd', fontWeight: 600, textAlign: 'center', fontSize: '0.9rem', color: 'var(--primary-color)' }}>Solicitado</div>

                        {/* Content - Tipo */}
                        <div style={{ backgroundColor: 'white', padding: '15px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                            <div style={{ padding: '4px 0', fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>Entrada</div>
                            <div style={{ padding: '4px 0', fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>Saída</div>
                            <div style={{ padding: '4px 0', fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>Entrada</div>
                            <div style={{ padding: '4px 0', fontSize: '0.9rem', color: '#666', fontWeight: 500 }}>Saída</div>
                        </div>

                        {/* Content - Original */}
                        <div style={{ backgroundColor: 'white', padding: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                            {[0, 1, 2, 3].map((idx) => (
                                <div key={idx} style={{ 
                                padding: '4px 12px', 
                                backgroundColor: originalTimesList[idx] ? '#f5f5f5' : 'transparent', 
                                borderRadius: '4px', 
                                fontFamily: 'monospace',
                                fontSize: '1rem',
                                color: originalTimesList[idx] ? '#555' : '#ccc',
                                minHeight: '28px',
                                display: 'flex',
                                alignItems: 'center'
                                }}>{originalTimesList[idx] || '-'}</div>
                            ))}
                            </div>
                        </div>

                        {/* Content - Solicitado */}
                        <div style={{ backgroundColor: 'white', padding: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                            {[0, 1, 2, 3].map((idx) => {
                                const time = localRequestedTimes[idx];
                                const isEditing = editingIndex === idx;

                                if (isEditing) {
                                    return (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '28px' }}>
                                            <input
                                                type="time"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={() => handleEditSave(idx)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleEditSave(idx);
                                                    if (e.key === 'Escape') handleEditCancel();
                                                }}
                                                autoFocus
                                                style={{
                                                    padding: '2px 4px',
                                                    borderRadius: '4px',
                                                    border: '1px solid var(--primary-color)',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9rem',
                                                    width: '90px'
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                return (
                                    <div key={idx} 
                                        style={{ 
                                            padding: '4px 12px', 
                                            backgroundColor: time ? '#e3f2fd' : 'transparent', 
                                            color: time ? 'var(--primary-color)' : '#ccc',
                                            borderRadius: '4px', 
                                            fontFamily: 'monospace', 
                                            fontSize: '1rem',
                                            fontWeight: time ? 600 : 400,
                                            minHeight: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            position: 'relative',
                                            cursor: !isReadOnly ? 'pointer' : 'default',
                                            border: !isReadOnly ? '1px dashed transparent' : 'none',
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => !isReadOnly && handleEditClick(idx)}
                                        onMouseEnter={(e) => !isReadOnly && (e.currentTarget.style.borderColor = 'var(--primary-color)')}
                                        onMouseLeave={(e) => !isReadOnly && (e.currentTarget.style.borderColor = 'transparent')}
                                        title={!isReadOnly ? "Clique para editar" : ""}
                                    >
                                        <span>{time || '-'}</span>
                                        {!isReadOnly && (
                                            <FiEdit2 size={12} style={{ opacity: 0.6 }} />
                                        )}
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                        </div>
                    </div>

                    <div className="full-width">
                        <Input
                            label="Motivo do Funcionário"
                            value={data.reason || ''}
                            readOnly
                            multiline
                            rows={3}
                            icon={FiFileText}
                            style={{ resize: 'none' }}
                        />
                    </div>

                    {data.attachment && (
                        <div className="full-width">
                        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Comprovante Anexado</label>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '1rem',
                            padding: '1rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px'
                        }}>
                            <FiPaperclip size={20} color="var(--primary-color)" />
                            <span style={{ flex: 1, fontSize: '0.9rem' }}>Comprovante disponível</span>
                            
                            {data.attachment.startsWith('data:image') && (
                                <button
                                    type="button"
                                    onClick={() => setShowPreviewModal(true)}
                                    style={{
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem', 
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--primary-color)',
                                        fontWeight: 500,
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        padding: '0'
                                    }}
                                >
                                    <FiEye size={18} /> Visualizar
                                </button>
                            )}

                            <a 
                            href={data.attachment} 
                            download={`comprovante_${data.id}`}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem', 
                                textDecoration: 'none', 
                                color: 'var(--primary-color)',
                                fontWeight: 500,
                                fontSize: '0.9rem'
                            }}
                            >
                            <FiDownload size={18} /> Baixar
                            </a>
                        </div>
                        </div>
                    )}
                </div>


            </div>

            {/* Right Column: Map */}
            <div>
                 {timeObjects.length > 0 && (
                    <div className="full-width">
                        <h3 className="section-title" style={{ marginTop: '0px' }}>Localização do Registro</h3>
                        
                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', overflowX: 'auto', paddingBottom: '4px' }}>
                            {timeObjects.map((obj, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => handleTabChange(index)}
                                disabled={mapLoading}
                                style={{
                                padding: '6px 14px',
                                borderRadius: '20px',
                                border: '1px solid',
                                borderColor: selectedTimeTab === index ? 'var(--primary-color)' : '#e0e0e0',
                                backgroundColor: selectedTimeTab === index ? 'var(--primary-color)' : 'white',
                                color: selectedTimeTab === index ? 'white' : '#666',
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                transition: 'all 0.2s',
                                opacity: mapLoading && selectedTimeTab !== index ? 0.7 : 1
                                }}
                            >
                                {obj.time}
                            </button>
                            ))}
                        </div>

                        {/* Status Header */}
                        {hasLocation && hasStation && (
                            <div style={{ 
                            marginBottom: '10px', 
                            padding: '10px', 
                            borderRadius: '6px', 
                            backgroundColor: isInsideFence ? '#f0fdf4' : '#fef2f2',
                            border: `1px solid ${isInsideFence ? 'var(--success-color)' : 'var(--error-color)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            color: isInsideFence ? 'var(--success-color)' : 'var(--error-color)',
                            fontWeight: 600
                            }}>
                            <FiMapPin />
                            {isInsideFence ? 'Dentro do posto de serviço' : 'Fora do posto de serviço'}
                            <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#666', marginLeft: 'auto' }}>
                                Distância: {(mapDistance * 1000).toFixed(0)}m (Raio: {((selectedTimeData.fence_radius_km || 0) * 1000).toFixed(0)}m)
                            </span>
                            </div>
                        )}

                        {/* Map Container */}
                        <div style={{ height: data.attachment ? '470px' : '375px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd', position: 'relative' }}>
                            <style>
                            {`
                                @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                                }
                            `}
                            </style>
                            {mapLoading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    zIndex: 1000,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: '10px'
                                }}>
                                    <div style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        border: '4px solid #f3f3f3', 
                                        borderTop: '4px solid var(--primary-color)', 
                                        borderRadius: '50%', 
                                        animation: 'spin 1s linear infinite' 
                                    }} />
                                    <span style={{ color: 'var(--primary-color)', fontWeight: 500 }}>Carregando mapa...</span>
                                </div>
                            )}
                            {hasLocation ? (
                            <MapContainer center={mapCenter} zoom={15} style={{ height: '100%', width: '100%' }}>
                                <MapUpdater bounds={mapBounds} center={mapCenter} />
                                <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                
                                {/* User Marker */}
                                <Marker position={[parseFloat(selectedTimeData.latitude), parseFloat(selectedTimeData.longitude)]}>
                                <Tooltip permanent direction="bottom">{(data.employee?.name || 'Funcionário').split(' ')[0]} (Registro: {selectedTimeData.time})</Tooltip>
                                </Marker>

                                {/* Station Marker and Fence */}
                                {hasStation && (
                                <>
                                    <Marker position={[parseFloat(selectedTimeData.station_latitude), parseFloat(selectedTimeData.station_longitude)]}>
                                    <Tooltip permanent direction="bottom">Posto de Serviço</Tooltip>
                                    </Marker>
                                    <Circle 
                                    center={[parseFloat(selectedTimeData.station_latitude), parseFloat(selectedTimeData.station_longitude)]}
                                    radius={(selectedTimeData.fence_radius_km || 0) * 1000}
                                    pathOptions={{ color: isInsideFence ? 'green' : 'red', fillColor: isInsideFence ? 'green' : 'red', fillOpacity: 0.1 }}
                                    />
                                </>
                                )}
                            </MapContainer>
                            ) : (
                            <div style={{ 
                                height: '100%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                backgroundColor: '#f9fafb', 
                                color: '#9ca3af',
                                flexDirection: 'column',
                                gap: '10px'
                            }}>
                                <FiMapPin size={30} />
                                <span>Localização não disponível para este registro</span>
                            </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>

        <h3 className="section-title" style={{ marginTop: '1.5rem' }}>Análise da Solicitação</h3>
        
        {error && <div className="form-error">{error}</div>}

        <div className="form-grid">
            <div className="full-width">
                <label style={{ fontSize: '0.9rem', fontWeight: 500, display: 'block', marginBottom: '0.75rem' }}>Decisão</label>
                <div className="analysis-decision-wrapper">
                <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '1rem', 
                    border: `1px solid ${analysis.status === 'approved' ? 'var(--success-color)' : 'var(--border-color)'}`,
                    borderRadius: '6px',
                    cursor: isReadOnly ? 'default' : 'pointer',
                    backgroundColor: analysis.status === 'approved' ? '#f0fdf4' : 'transparent',
                    opacity: isReadOnly && analysis.status !== 'approved' ? 0.5 : 1
                }}>
                    <input 
                    type="radio" 
                    name="status" 
                    value="approved" 
                    checked={analysis.status === 'approved'} 
                    onChange={() => handleStatusChange('approved')}
                    disabled={isReadOnly}
                    style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FiCheckCircle color="var(--success-color)" size={20} />
                    <span style={{ fontWeight: 500, color: 'var(--success-color)' }}>Aprovar Solicitação</span>
                    </div>
                </label>

                <label style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '1rem', 
                    border: `1px solid ${analysis.status === 'rejected' ? 'var(--error-color)' : 'var(--border-color)'}`,
                    borderRadius: '6px',
                    cursor: isReadOnly ? 'default' : 'pointer',
                    backgroundColor: analysis.status === 'rejected' ? '#fef2f2' : 'transparent',
                    opacity: isReadOnly && analysis.status !== 'rejected' ? 0.5 : 1
                }}>
                    <input 
                    type="radio" 
                    name="status" 
                    value="rejected" 
                    checked={analysis.status === 'rejected'} 
                    onChange={() => handleStatusChange('rejected')}
                    disabled={isReadOnly}
                    style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FiXCircle color="var(--error-color)" size={20} />
                    <span style={{ fontWeight: 500, color: 'var(--error-color)' }}>Reprovar Solicitação</span>
                    </div>
                </label>
                </div>
            </div>

            {(analysis.status === 'rejected' || (isReadOnly && analysis.rejection_reason)) && (
                <div className="full-width" ref={rejectionReasonRef}>
                <Input
                    label="Motivo da Reprovação"
                    value={analysis.rejection_reason}
                    onChange={handleReasonChange}
                    placeholder="Descreva o motivo da reprovação..."
                    required={analysis.status === 'rejected'}
                    disabled={isReadOnly}
                    multiline
                    rows={4}
                    style={{ resize: 'none' }}
                />
                </div>
            )}
        </div>

      </div>

      <div className="form-actions">
        <Button 
          type="button" 
          className="cancel-button" 
          onClick={onCancel}
          disabled={loading}
        >
          {isReadOnly ? 'Fechar' : 'Cancelar'}
        </Button>
        {!isReadOnly && (
          <Button type="submit" loading={loading}>
            Salvar
          </Button>
        )}
        {isReadOnly && (
          <Button 
            type="button" 
            onClick={() => setShowRevertModal(true)}
            loading={loading}
            style={{  
              backgroundColor: '#fff7ed', 
              color: '#ea580c', 
              borderColor: '#fdba74'
            }}
          >
            <FiRotateCcw style={{ marginRight: '8px' }} />
            Reverter para Análise
          </Button>
        )}
      </div>
    </form>
  );
}